import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getApiError } from '../utils/apiError';

interface CrudHandlersConfig<T extends { id: number | string }> {
  /** Table hook instance (must expose data/setData) */
  table: {
    data: T[];
    setData: (data: T[]) => void;
  };
  /** Service create function */
  createFn?: (data: any) => Promise<T>;
  /** Service update function */
  updateFn?: (id: number | string, data: any) => Promise<T>;
  /** Service delete function */
  deleteFn?: (id: number | string) => Promise<void>;
  /** Entity name for toast messages (e.g. "Clinic") */
  entityName: string;
  /** Called after successful create/update (e.g. close dialog, reset form) */
  onSuccess?: () => void;
  /** Called after successful delete (e.g. close dialog, clear selection) */
  onDeleteSuccess?: () => void;
  /** Optional: reload data instead of local state update after create/update */
  reloadData?: () => void;
}

export function useCrudHandlers<T extends { id: number | string }>(config: CrudHandlersConfig<T>) {
  const {
    table,
    createFn,
    updateFn,
    deleteFn,
    entityName,
    onSuccess,
    onDeleteSuccess,
    reloadData,
  } = config;

  const handleCreateOrUpdate = useCallback(async (
    data: any,
    editingItem: T | null,
  ) => {
    try {
      if (editingItem && updateFn) {
        const updated = await updateFn(editingItem.id, data);
        if (reloadData) {
          reloadData();
        } else {
          table.setData(table.data.map(item => item.id === editingItem.id ? updated : item));
        }
        toast.success(`${entityName} updated successfully`);
      } else if (createFn) {
        const created = await createFn(data);
        if (reloadData) {
          reloadData();
        } else {
          table.setData([...table.data, created]);
        }
        toast.success(`${entityName} created successfully`);
      }
      onSuccess?.();
    } catch (error: any) {
      console.error(`Error saving ${entityName.toLowerCase()}:`, error);
      toast.error(getApiError(error, 'An error occurred') || `Failed to save ${entityName.toLowerCase()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.data, entityName, createFn, updateFn, onSuccess, reloadData]);

  const handleDelete = useCallback(async (item: T | null) => {
    if (!item || !deleteFn) return;

    try {
      await deleteFn(item.id);
      if (reloadData) {
        reloadData();
      } else {
        table.setData(table.data.filter(d => d.id !== item.id));
      }
      toast.success(`${entityName} deleted successfully`);
      onDeleteSuccess?.();
    } catch (error: any) {
      console.error(`Error deleting ${entityName.toLowerCase()}:`, error);
      toast.error(getApiError(error, 'An error occurred') || `Failed to delete ${entityName.toLowerCase()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table.data, entityName, deleteFn, onDeleteSuccess, reloadData]);

  return { handleCreateOrUpdate, handleDelete };
}
